import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { MemberAvailabilityBlock, WeeklyAvailability } from '../types/availability';

// Data-only state
interface AvailabilityDataState {
  weeklyAvailability: WeeklyAvailability | null;
  monthlyAvailability: MemberAvailabilityBlock[] | null;
  globalHours: Record<string, { start_time: string; end_time: string }>;
  loading: boolean;
  error: string | null;
}

// Data-only actions
type AvailabilityDataAction =
  | { type: 'SET_WEEKLY_AVAILABILITY'; payload: WeeklyAvailability }
  | { type: 'SET_MONTHLY_AVAILABILITY'; payload: MemberAvailabilityBlock[] }
  | { type: 'SET_GLOBAL_HOURS'; payload: Record<string, { start_time: string; end_time: string }> }
  | { type: 'ADD_AVAILABILITY_BLOCK'; payload: MemberAvailabilityBlock }
  | { type: 'UPDATE_AVAILABILITY_BLOCK'; payload: MemberAvailabilityBlock }
  | { type: 'REMOVE_AVAILABILITY_BLOCK'; payload: number }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null };

const initialDataState: AvailabilityDataState = {
  weeklyAvailability: null,
  monthlyAvailability: null,
  globalHours: {},
  loading: false,
  error: null,
};

function availabilityDataReducer(state: AvailabilityDataState, action: AvailabilityDataAction): AvailabilityDataState {
  switch (action.type) {
    case 'SET_WEEKLY_AVAILABILITY':
      return { ...state, weeklyAvailability: action.payload };

    case 'SET_MONTHLY_AVAILABILITY':
      return { ...state, monthlyAvailability: action.payload };

    case 'SET_GLOBAL_HOURS':
      return { ...state, globalHours: action.payload };
      
    case 'ADD_AVAILABILITY_BLOCK':
      if (!state.weeklyAvailability) return state;
      return {
        ...state,
        weeklyAvailability: {
          ...state.weeklyAvailability,
          availability_blocks: [...state.weeklyAvailability.availability_blocks, action.payload],
        },
      };
      
    case 'UPDATE_AVAILABILITY_BLOCK':
      if (!state.weeklyAvailability) return state;
      return {
        ...state,
        weeklyAvailability: {
          ...state.weeklyAvailability,
          availability_blocks: state.weeklyAvailability.availability_blocks.map(block =>
            block.id === action.payload.id ? action.payload : block
          ),
        },
      };
      
    case 'REMOVE_AVAILABILITY_BLOCK':
      if (!state.weeklyAvailability) return state;
      return {
        ...state,
        weeklyAvailability: {
          ...state.weeklyAvailability,
          availability_blocks: state.weeklyAvailability.availability_blocks.filter(
            block => block.id !== action.payload
          ),
        },
      };

    case 'SET_LOADING':
      return { ...state, loading: action.payload };
      
    case 'SET_ERROR':
      return { ...state, error: action.payload };

    default:
      return state;
  }
}

interface AvailabilityDataContextType {
  state: AvailabilityDataState;
  dispatch: React.Dispatch<AvailabilityDataAction>;
}

const AvailabilityDataContext = createContext<AvailabilityDataContextType | undefined>(undefined);

interface AvailabilityDataProviderProps {
  children: ReactNode;
}

export const AvailabilityDataProvider: React.FC<AvailabilityDataProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(availabilityDataReducer, initialDataState);

  return (
    <AvailabilityDataContext.Provider value={{ state, dispatch }}>
      {children}
    </AvailabilityDataContext.Provider>
  );
};

export const useAvailabilityData = () => {
  const context = useContext(AvailabilityDataContext);
  if (context === undefined) {
    throw new Error('useAvailabilityData must be used within an AvailabilityDataProvider');
  }
  return context;
};

export type { AvailabilityDataState, AvailabilityDataAction };